/**
 * These navigation guards can be registered on router routes.
 * They can be used to redirect to another route or load data.
 */

import { reactive } from "vue";
import envVars from "@/helpers/Env";
import * as SaveService from "@/api/SaveService.js";
import eventHub from "@/helpers/utility/Events";
import { i18n } from "@/i18n";
import { InvalidTokenError, NoTokenError } from "@core/errors/Errors";

import { useUser } from "@/api/user.store";
import { useWorkspaces } from "@/api/workspaces.store";
import { useSharedModels } from "@/api/sharedModels.store";
import { usePublicModels } from "@/api/publicModels.store";
import { useOrganizations } from "@manager/organizations/organizations.store";
import { useModels } from "@/api/models.store";
import { useLoadedModel } from "@editor/store/loadedModel.store";
import { showError } from "@core/ui/alert/alert";
import { useProjectStore } from "@editor/store/project.store";

import type { RouteLocationNormalized, RouteLocationRaw } from "vue-router";
import type { NavigationGuard } from "./routeTypes";
import { useLicense } from "@/api/license.store";
import encryptor from "@/helpers/data/Encryptor";

const { getOrLoadUser, user, isSyncEnabled } = useUser();
const { getOrganization } = useOrganizations({}, { user });

export const softwareIsNotExpired: NavigationGuard = {
  name: "softwareIsNotExpired",
  enter(to) {
    if (!envVars.expires) return true;

    console.log(
      "%cChecking if software is expired...",
      "padding: 2px 4px; background-color: palegreen;",
      to.name
    );

    // get expiration date
    const now = Date.now();
    const expiredAt = Date.parse(envVars.expirationDate);

    if (now > expiredAt) {
      return { name: "softwareExpired" };
    }
  }
};

export const isAuthenticated: NavigationGuard = {
  name: "isAuthenticated",
  async update(to, _from) {
    console.log(
      "%cCheck Auth...",
      "padding: 2px 4px; background-color: bisque;",
      to.name
    );

    if (!envVars.tokenRequired) {
      console.log("Granting access without token...");
      return true;
    }

    if (to.query.uid && to.query.token) {
      return {
        name: "magic-login",
        params: {
          uid: to.query.uid as string,
          token: to.query.token as string,
          type: (to.query?.t as string) ?? null,
          relatedPk: (to.query?.r as string) ?? null,
          redirect: to.fullPath
        }
      } satisfies RouteLocationRaw;
    } else {
      // Load user if not loaded:
      try {
        const user = await getOrLoadUser();

        if (
          !user.verified ||
          user.acceptedTermsAndPrivacyPolicy == null
        ) {
          if (to.name !== "onboarding-password") {
            return {
              name: "onboarding-password"
            };
          } else return true;
        } else if (!user.name) {
          if (to.name !== "onboarding-name") {
            return {
              name: "onboarding-name"
            };
          } else return true;
        }

        // Check sync setting:
        if (envVars.synchronizeSaves) {
          const { syncEnabled } = useUser();
          if (syncEnabled.value == null) {
            if (to.name !== "onboarding-synchronization") {
              return {
                name: "onboarding-synchronization"
              };
            }
          }
        }

        console.log("user loaded", user);
      } catch (error) {
        console.warn("Could not get user.");
        console.error((error as Error).message);
        return {
          name: "login",
          params: { redirect: to.fullPath }
        };
      }
    }
  }
};

export const hasValidLicense: NavigationGuard = {
  name: "hasValidLicense",
  async update(to) {
    console.log(
      "%cCheck License...",
      "padding: 2px 4px; background-color: lightpink;",
      to.name
    );
    const license = useLicense();
    const {
      activeOrganizationLicenses,
      acceptedOrganizationLicenses,
      pendingOrganizationLicenses
    } = useOrganizations({}, { user });

    if (activeOrganizationLicenses.value.length > 0) {
      return true;
    }

    // Check license
    if (license.nonExpiredLicenses.length == 0) {
      // if the user has any pending organization invitations, redirect them to the accept page:
      if (pendingOrganizationLicenses.value.length > 0) {
        return {
          name: "organization-join",
          params: {
            organizationSlug: pendingOrganizationLicenses.value[0].slug
          }
        };
      }

      if (acceptedOrganizationLicenses.value.length > 0) {
        // User had an organization license, but it expired
        return {
          name: "settings-subscriptions"
        };
      }

      if (license.all.length == 0) {
        console.warn("User has no license.");
        console.log(user);
        return {
          name: "onboarding-license"
        };
      }

      return {
        name: "settings-subscriptions"
      };
    }
  }
};

export const preloadPersonalWorkspace: NavigationGuard = {
  name: "preloadPersonalWorkspace",
  async update(to, from) {
    const workspaceId = user.value?.workspace?.id;

    if (workspaceId == null) {
      console.warn("user has no workspace!");
      return false;
    }

    const { loadWorkspace, userWorkspace } = useWorkspaces({});

    if (userWorkspace.value == null || userWorkspace.value.folders == null) {
      try {
        console.log(
          "%cPreload user workspace",
          "background-color: lightyellow; padding: 0.5em;"
        );
        await loadWorkspace(workspaceId);
      } catch (error) {
        console.log("error preloading workspace", error);
        if (from.matched.length > 0) return false;
        return {
          name: "personal-workspace"
        };
      }
    } else {
      console.log(
        "%cUpdate user workspace",
        "background-color: lightyellow; padding: 0.5em;"
      );

      loadWorkspace(workspaceId).catch((error) => {
        console.warn(error);
      });
    }
  }
};

/**
 * Load the workspace set by the slugs in the URL
 */
export const preloadTeam: NavigationGuard = {
  name: "preloadTeam",
  async update(to, from) {
    const organizationSlug = to.params.organizationSlug as string;
    const teamSlug = to.params.teamSlug as string;

    const fromOrganizationSlug = from.params.organizationSlug;
    const fromWorkspaceSlug = from.params.teamSlug;

    if (
      fromOrganizationSlug === organizationSlug &&
      fromWorkspaceSlug === teamSlug
    ) {
      return true;
    }

    // update workspace in background if data is already here
    const { workspacesBySlug, loadTeam } = useWorkspaces();
    const workspace = workspacesBySlug.value?.[teamSlug];

    if (workspace) {
      console.log(
        "%cUpdate Team (in background)",
        "background-color: lightyellow; padding: 0.5em;",
        organizationSlug,
        teamSlug
      );

      try {
        loadTeam(organizationSlug, teamSlug);
      } catch (_error) {
        const error = _error as Error & { status?: number };
        console.error(error.name, error?.status, error.message);
      }
    } else {
      console.log(
        "%cPreload Team",
        "background-color: lightyellow; padding: 0.5em;",
        organizationSlug,
        teamSlug
      );

      try {
        await loadTeam(organizationSlug, teamSlug);
      } catch (error) {
        console.log("error preloading workspace", error);
        showError(i18n.t("errors.noWorkspaceAccess"));

        if (from.matched.length > 0 && from.name != "login") {
          return false;
        }
        return {
          name: "personal-workspace"
        };
      }
    }
  }
};

export const hasJoinedWorkspace: NavigationGuard = {
  name: "hasJoinedWorkspace",
  async update(to) {
    const organizationSlug = to.params.organizationSlug as string;
    const teamSlug = to.params.teamSlug as string;
    const { slugWorkspace } = useWorkspaces(to.params);

    // Check my workspace access, make sure user has actively joined
    const myWorkspaceAccess = slugWorkspace.value?.membership;

    if (!myWorkspaceAccess) {
      // check if user has a organization access that allows them to view workspace anyway:
      try {
        // load organization first to make sure we have access to the organization:
        const { getOrganization, currentOrganizationRole } = useOrganizations(
          {
            organizationSlug: organizationSlug
          },
          { user }
        );
        await getOrganization(organizationSlug);

        // allow workspace access if the organization role of the user allows them to edit the workspace
        if (
          currentOrganizationRole.value &&
          currentOrganizationRole.value.canManageTeams
        ) {
          return true;
        }
      } catch (_error) {
        const error = _error as Error & { status?: number };

        if (error.status == null) {
          console.warn("could not get organization (no connection)");
        } else {
          console.error(error);
        }
      }

      showError(i18n.t("errors.noWorkspaceAccess"));

      return {
        name: "settings-organization",
        params: {
          organizationSlug: organizationSlug
        }
      };
    }

    if (!myWorkspaceAccess.accepted) {
      console.warn("Workspace access not accepted yet", {
        ...myWorkspaceAccess
      });
      return {
        name: "team-join",
        params: {
          organizationSlug: organizationSlug,
          teamSlug: teamSlug
        }
      };
    }
  }
};

export const loadWorkspaces: NavigationGuard = {
  name: "loadWorkspaces",
  enter() {
    const { loadWorkspaces } = useWorkspaces();
    loadWorkspaces();
  }
};

export const loadWorkspaceModelInfo: NavigationGuard = {
  name: "loadWorkspaceModelInfo",
  update(to) {
    const { getModelInfo, synchModel } = useModels();

    const modelId = to.params.modelId as string;

    console.log("[navguard] get model info");
    getModelInfo(modelId)
      .then((model) => {
        console.log(
          "%cWorkspace model loaded",
          "background-color: lightblue; padding: 0.5em;",
          model
        );
      })
      .catch((error) => {
        console.log(error);
      });

    if (isSyncEnabled()) {
      synchModel(modelId).catch((error) => {
        console.warn(error);
      });
    }
  }
};

/**
 * Makes sure that when a workspace is opened,
 * the first folder in it is opened too.
 */
export const forceOpenWorkspaceFolder: NavigationGuard = {
  name: "forceOpenWorkspaceFolder",
  update(to) {
    if (to.params?.folderSlug == null) {
      const { currentWorkspaceFolders } = useWorkspaces(to.params);
      const firstFolder = currentWorkspaceFolders.value?.[0];
      if (!firstFolder) return true;
      return `${to.fullPath}/${firstFolder.slug}`;
    }
  }
};

export const loadSharedModelInfo: NavigationGuard = {
  name: "loadSharedModelInfo",
  async update(to) {
    const { getSharedModelInfo } = useSharedModels();

    const shareId = parseInt(to.params.shareId as string);
    getSharedModelInfo(shareId);
  }
};

export const preloadModel: NavigationGuard = {
  name: "preloadModel",
  /**
   * Get the model info from a workspace
   */
  async update(to) {
    const { currentWorkspace } = useWorkspaces(to.params);
    const { loadedModel } = useLoadedModel();
    const { getModelInfo, getModelData, synchModel } = useModels();
    const workspaceId = currentWorkspace.value?.id;
    const modelId = to.params.modelId as string;

    try {
      if (workspaceId == null) {
        throw new Error("Workspace not loaded");
      }
      // ONLY load the model if it is not already loaded.
      if (
        loadedModel.type !== "workspace_model" ||
        `${loadedModel.id}` !== `${modelId}`
      ) {
        const progressTracker = reactive({});
        eventHub.emit("loading-started", { progressTracker });

        const [modelInfo, modelData] = await Promise.all([
          getModelInfo(modelId, { progressTracker }),
          getModelData(workspaceId, modelId, { progressTracker })
        ]);

        if (isSyncEnabled()) {
          synchModel(modelId).catch((error) => {
            console.warn(error);
          });
        }

        await SaveService.loadInfoAndData(modelInfo, modelData);
        eventHub.emit("loading-ended");
      }
    } catch (_error) {
      const error = _error as Error & { status?: number };
      console.log("error during model loading", error);
      eventHub.emit("loading-ended");
      useLoadedModel().setLoadingError(error);
      return getRouteForModelLoadingError(error, to);
    }
  }
};

export const inModelGuard: NavigationGuard = {
  name: "inModelGuard",
  /**
   * Makes sure the loaded model is closed as soon as the current model
   * area is left
   */
  async leave() {
    const { resetLoadedModel } = useLoadedModel();
    resetLoadedModel();
  }
};

export const preloadPublication: NavigationGuard = {
  name: "preloadPublication",
  async update(to) {
    try {
      const publicationId = to.params.publicationId as string;

      const { loadedModel } = useLoadedModel();

      if (
        loadedModel.type !== "public_model" ||
        loadedModel.id !== publicationId
      ) {
        const { getPublicModelInfo, getPublicModelData } = usePublicModels();

        const progressTracker = reactive({});
        eventHub.emit("loading-started", { progressTracker });

        const modelInfo = await getPublicModelInfo(publicationId, {
          progressTracker
        });
        const modelData = await getPublicModelData(publicationId, {
          progressTracker
        });

        // When loading a public model, we don't use loadInfoAndData,
        // because we apply some data filtering first (removing hidden scenes).
        // Instead, we do the loading process step by step here:

        // DECRYPT DATA
        encryptor.reset();
        const decryptedData = await encryptor.decryptAskingForPassword(
          modelData.data
        );

        // Data filtering:
        // Remove scenes hidden in publication:
        const scenesToHide = modelInfo.hiddenScenes || [];
        if (scenesToHide.length > 0) {
          const sceneItems = decryptedData.scenes?.items || {};
          const order = decryptedData.scenes?.order || [];
          for (const sceneId of scenesToHide) {
            delete sceneItems[sceneId];
            const index = order.indexOf(sceneId);
            if (index > -1) {
              order.splice(index, 1);
            }
          }
        }

        // Load the model now:
        await SaveService.loadData(decryptedData, {
          media: modelData.media
        });
        useLoadedModel().setLoadedModel({
          ...modelInfo,
          accessableAreas: modelInfo.accessableAreas ? [...modelInfo.accessableAreas] : null,
          hiddenScenes: modelInfo.hiddenScenes ? [...modelInfo.hiddenScenes] : null,
          lastVersion: { id: modelData.id },
          workspace: undefined,
          folder: undefined
        });
      }
    } catch (_error) {
      const error = _error as Error & { status?: number };
      console.warn("error during model loading", error);
      useLoadedModel().setLoadingError(error);
      return getRouteForModelLoadingError(error, to);
    } finally {
      eventHub.emit("loading-ended");
    }
  }
};

export const preloadShare: NavigationGuard = {
  name: "preloadShare",
  async update(to) {
    try {
      const shareId = parseInt(to.params.shareId as string);

      const { loadedModel } = useLoadedModel();

      if (loadedModel.type != "shared_model" || loadedModel.id != shareId) {
        const progressTracker = reactive({});
        eventHub.emit("loading-started", { progressTracker });

        const { getSharedModelData, getSharedModelInfo } = useSharedModels();
        const modelInfo = await getSharedModelInfo(shareId, {
          progressTracker
        });
        const modelData = await getSharedModelData(shareId, {
          progressTracker
        });
        await SaveService.loadInfoAndData(
          {
            ...modelInfo,
            workspace: undefined,
            folder: undefined
          },
          modelData
        );

        eventHub.emit("loading-ended");
      }
    } catch (_error) {
      const error = _error as Error & { status?: number };

      console.log("error during model loading");
      eventHub.emit("loading-ended");
      useLoadedModel().setLoadingError(error);
      return getRouteForModelLoadingError(error, to);
    }
  }
};

/**
 * Returns the vue-router route that should be called
 * if there was any problem when loading a model
 */
function getRouteForModelLoadingError(
  error: Error,
  to: RouteLocationNormalized
) {
  if (error instanceof InvalidTokenError || error instanceof NoTokenError) {
    console.warn(`${error.name}`, error.message);
    return { name: "login", params: { redirect: to.fullPath } };
  }
  return undefined;
}

const allEditorAreas = [
  "model",
  "variables",
  "simulation",
  "inspector",
  "evaluation"
];

export const guardEditorArea: NavigationGuard = {
  name: "guardEditorArea",
  update(to, from) {
    // don't redirect if there's no loaded model:
    const { loadedModel } = useLoadedModel();
    if (loadedModel?.id == null) return true;

    if (canAccessEditorArea(to.meta?.areaName as string)) return true;
    else if (
      allEditorAreas.includes(from.meta?.areaName as string) &&
      JSON.stringify(from.params) === JSON.stringify(to.params)
    ) {
      return false;
    } else {
      const pathParts = to.fullPath.split("/");
      if (allEditorAreas.includes(pathParts[pathParts.length - 1])) {
        const newPath = pathParts
          .slice(0, -1)
          .concat(getAccessableEditorAreas()[0])
          .join("/");
        return { path: newPath };
      } else {
        const newPath = pathParts
          .slice(0)
          .concat(getAccessableEditorAreas()[0])
          .join("/");
        return { path: newPath };
      }
    }
  }
};

function getAccessableEditorAreas() {
  let editorAreas = allEditorAreas.slice();

  const loadedModel = useLoadedModel().loadedModel;

  if (loadedModel.accessableAreas) {
    editorAreas = loadedModel.accessableAreas;
  }

  // 1. make sure that user has access to the requested area.

  // Make sure that the model can be viewed in that area.
  // prevent navigating to editable areas when in player model
  if (useProjectStore().isReadOnly) {
    editorAreas = editorAreas.filter(
      (area) => area != "model" && area != "variables"
    );
  }
  return editorAreas;
}

/**
 *
 */
function canAccessEditorArea(editorArea?: string) {
  if (editorArea == null) return false;
  const accessableAreas = getAccessableEditorAreas();

  console.log(
    `%cChecking access to ${editorArea}`,
    "padding: 2px 4px; background-color: lightblue;",
    accessableAreas
  );

  if (accessableAreas.length === 0) {
    // No access to any area? Prevent navigation.
    return false;
  }
  return accessableAreas.includes(editorArea);
}

export const preloadOrganization: NavigationGuard = {
  name: "preloadOrganization",
  async update(to) {
    const organizationSlug = to.params.organizationSlug as string;

    const { currentOrganization } = useOrganizations(
      { organizationSlug: organizationSlug },
      { user }
    );
    const organization = currentOrganization.value;

    if (organization) {
      console.log(
        "%cUpdate Organization",
        "background-color: antiquewhite; padding: 0.5em;",
        organizationSlug
      );

      getOrganization(organizationSlug);
    } else {
      console.log(
        "%cPreload Organization",
        "background-color: antiquewhite; padding: 0.5em;",
        organizationSlug
      );

      try {
        await getOrganization(organizationSlug);
      } catch (error) {
        if (to.name != "settings-organization") {
          return {
            name: "settings-organization",
            params: { organizationSlug: organizationSlug }
          };
        }
      }
    }
  }
};

export const loadOrganizationMembers: NavigationGuard = {
  name: "loadOrganizationMembers",
  async update(to) {
    let organizationSlug = to.params.organizationSlug;
    if (Array.isArray(organizationSlug)) {
      organizationSlug = organizationSlug[0];
    }
    const { currentOrganization, getOrganizationMembers } = useOrganizations(
      { organizationSlug },
      { user }
    );
    if (currentOrganization.value) {
      getOrganizationMembers(currentOrganization.value);
    }
  }
};
export const loadTeams: NavigationGuard = {
  name: "loadTeams",
  async update(to) {
    let organizationSlug = to.params.organizationSlug;
    if (Array.isArray(organizationSlug)) {
      organizationSlug = organizationSlug[0];
    }
    const { currentOrganization, getTeams } = useOrganizations(
      { organizationSlug },
      { user }
    );
    if (currentOrganization.value) {
      if (currentOrganization.value.expired) {
        return;
      }
      getTeams(currentOrganization.value);
    }
  }
};

export const isUserAdmin: NavigationGuard = {
  name: "isUserAdmin",
  async enter(to) {
    console.log(
      "%cCheck Admin...",
      "padding: 2px 4px; background-color: lightblue;",
      to.name
    );
    if (user.value?.isGlobalAdmin) return true;
    if (user.value?.isUserAdmin) return true;
    return {
      name: "personal-workspace"
    };
  }
};
